import type { RegionRequest, StopPreview, TourSummary } from '../../shared/types.js';
import { clear, el, hex, icon } from './dom.js';
import { languageSwitch } from './languageSwitch.js';
import type { IconName } from './icons.js';
import { buildStopPreview, previewSvg } from './tourPreview.js';
import { CAR_COLORS, type FixtureInfo } from './lobbyScreen.js';
import { num, plural, serverText, t } from '../i18n/i18n.js';
import type { PreviewResult } from './regionPicker.js';

/**
 * The front door.
 *
 * What replaced the old home screen, and the reason for most of this rework. That screen
 * asked for a name and a colour before it had given anybody a reason to care, and then
 * dropped them into a lobby where the actual choice — *where are we racing* — was buried
 * three panels down a scrolling column. The order was backwards: nobody arrives wanting to
 * fill in a form, they arrive wanting to look at what there is.
 *
 * So this is a shelf of things to race, and everything else waits its turn. Pick an entry,
 * and only then does it ask who you are — once, and never again, because the answer is kept.
 *
 * In front of the shelf is one plain question — are you starting a lobby or joining one —
 * because those are the only two reasons anybody arrives, and the second used to be a
 * four-character box in the corner of the masthead that people simply did not find. See
 * DECISIONS.md §60.
 *
 * The paper metaphor is not decoration. A tour is genuinely a document: an ordered list of
 * places with distances against them, which is what a rally roadbook is, and it turns out
 * that a card with a hand-drawn map and a typewritten caption tells you more about a race
 * than any amount of chrome.
 */

export type Tab = 'tours' | 'quick' | 'yours';

/** The question first, then — for somebody hosting — the shelf. */
export type Stage = 'choose' | 'create';

/** What somebody picked off the shelf. The lobby is created around it. */
export type Pick =
  | { kind: 'tour'; id: string; title: string }
  | { kind: 'region'; region: RegionRequest; title: string };

export interface FrontDoorOptions {
  initialName: string;
  initialColor: number;
  initialCode: string;
  onStart(pick: Pick, name: string, color: number): void;
  onJoin(code: string, name: string, color: number): void;
  onNewTour(): void;
  onEditTour(id: string): void;
  /**
   * "Draw your own": the shell opens the race wizard, and hands the result back through
   * `chooseRegion`. Drawn before the lobby exists rather than after, so that changing your
   * mind halfway leaves you on this shelf instead of alone in an empty room.
   */
  onDraw(): void;
  /** Used only to draw a fixture's sketch; a failure here costs a picture, nothing more. */
  onPreview(region: RegionRequest): Promise<PreviewResult>;
  onSignIn(): void;
  /** The docket changed the name or colour. The app keeps it; the pill shows it. */
  onDriverChange(name: string, color: number): void;
  /** A thumb up (1), down (-1) or taken back (0). Only ever called while signed in. */
  onVote(tourId: string, vote: -1 | 0 | 1): Promise<VoteResult>;
}

export type VoteResult = { up: number; down: number; myVote: -1 | 0 | 1 } | { error: string };

/**
 * How many of the most popular tours the opening screen shows under the two questions.
 * One row on a laptop; the rest are a click away on the shelf.
 */
const POPULAR_COUNT = 4;

const TABS: { key: Tab; label: string }[] = [
  { key: 'tours', label: t('Tours') },
  { key: 'quick', label: t('Quick race') },
  { key: 'yours', label: t('Yours') },
];

export class FrontDoor {
  readonly root: HTMLElement;
  /**
   * The shell parks the leaderboard and driver pills here while this screen is up. The
   * driver pill is the account panel's — name, colour and account in one place — so this
   * screen no longer has a chip of its own.
   */
  readonly badgeSlot = el('div', { class: 'masthead-badges' });

  private readonly shelf = el('div', { class: 'shelf', id: 'shelf' });
  private readonly tabStrip = el('div', { class: 'sheet-tabs' });
  private readonly errorNode = el('p', { class: 'form-error sheet-error', id: 'home-error' });
  private readonly codeInput: HTMLInputElement;
  private readonly searchInput: HTMLInputElement;

  private readonly docketVeil: HTMLElement;
  private readonly docket: HTMLElement;
  private readonly docketHead = el('h2', { class: 'docket-head' }, t('Race entry'));
  private readonly docketFor = el('p', { class: 'docket-for' });
  private readonly docketError = el('p', { class: 'form-error' });
  private readonly nameInput: HTMLInputElement;
  private readonly swatches = el('div', { class: 'swatches' });
  private readonly docketGo: HTMLButtonElement;

  private readonly chooser: HTMLElement;
  private readonly createView: HTMLElement;
  private readonly popular = el('section', { class: 'front-popular is-hidden', id: 'front-popular' });
  private stage: Stage = 'choose';
  private tab: Tab = 'tours';
  private fixtures: FixtureInfo[] = [];
  private tours: TourSummary[] = [];
  private signedIn = false;
  /** Once somebody has picked a tab themselves, arriving data never moves it. */
  private touched = false;
  private name: string;
  private color: number;
  private query = '';

  /**
   * Sketches for the built-in regions, drawn from a dry run of world generation.
   *
   * A tour stores its own sketch (see `tourPreview.ts`), but a fixture is just a bounding
   * box in a list — there is nothing to draw until somebody has generated it. So the first
   * time the quick-race shelf is opened it asks the server to generate each one and keeps
   * the road lines that come back. They are cached on disk after the first go, and a card
   * whose sketch never arrives simply keeps its ruled paper.
   */
  private readonly sketches = new Map<string, StopPreview>();
  private readonly artNodes = new Map<string, HTMLElement>();
  private sketching = false;

  /** What the docket will do when it is confirmed. */
  private pending: { mode: 'start'; pick: Pick } | { mode: 'join'; code: string } | null =
    null;

  constructor(private readonly options: FrontDoorOptions) {
    this.name = options.initialName;
    this.color = options.initialColor;

    this.codeInput = el('input', {
      class: 'field field-code',
      type: 'text',
      maxLength: 4,
      placeholder: '····',
      value: options.initialCode,
      id: 'lobby-code',
      autocapitalize: 'characters',
      autocomplete: 'off',
    });
    this.codeInput.addEventListener('input', () => {
      this.codeInput.value = this.codeInput.value.toUpperCase().replace(/[^A-Z0-9]/g, '');
    });

    const joinButton = el('button', { class: 'button button-primary', type: 'button', id: 'join-button' }, t('Join'), icon('arrow_forward'));
    joinButton.addEventListener('click', () => this.join());
    // Enter in the code box is the same as pressing Join; anything else there is a trap.
    this.codeInput.addEventListener('keydown', (event) => {
      if (event.key === 'Enter') {
        event.preventDefault();
        this.join();
      }
    });

    this.searchInput = el('input', {
      class: 'field field-search',
      type: 'search',
      id: 'shelf-search',
      placeholder: t('Search tours, cities, people'),
      autocomplete: 'off',
    });
    this.searchInput.addEventListener('input', () => {
      this.query = fold(this.searchInput.value.trim());
      this.renderShelf();
    });

    for (const { key, label } of TABS) {
      const tab = el(
        'button',
        { class: 'sheet-tab', type: 'button', dataset: { tab: key }, id: `tab-${key}` },
        label,
        el('span', { class: 'sheet-tab-count' }, ''),
      );
      tab.addEventListener('click', () => {
        this.touched = true;
        this.select(key);
      });
      this.tabStrip.append(tab);
    }

    // --- the docket ------------------------------------------------------
    this.nameInput = el('input', {
      class: 'field',
      type: 'text',
      maxLength: 16,
      placeholder: t('Who is driving?'),
      value: this.name,
      id: 'player-name',
    });
    this.nameInput.addEventListener('keydown', (event) => {
      if (event.key === 'Enter') {
        event.preventDefault();
        this.confirm();
      }
    });
    this.renderSwatches();

    this.docketGo = el(
      'button',
      { class: 'button button-primary button-lead', type: 'button', id: 'docket-go' },
      t('Start a lobby'),
    );
    this.docketGo.addEventListener('click', () => this.confirm());

    const cancel = el('button', { class: 'button button-ghost', type: 'button', id: 'docket-cancel' }, t('Cancel'));
    const signInLink = el('button', { class: 'link-button', type: 'button', id: 'docket-sign-in' }, t('Sign in'));
    signInLink.addEventListener('click', () => this.options.onSignIn());
    cancel.addEventListener('click', () => this.closeDocket());

    this.docket = el(
      'div',
      { class: 'docket', id: 'docket' },
      this.docketHead,
      this.docketFor,
      el(
        'div',
        { class: 'docket-fields' },
        el('label', { class: 'field-label', htmlFor: 'player-name' }, t('Driver name'), this.nameInput),
        el('div', { class: 'field-label' }, t('Car colour'), this.swatches),
      ),
      this.docketError,
      el('div', { class: 'docket-actions' }, this.docketGo, cancel),
      el('p', { class: 'docket-account' }, t('Have an account? '), signInLink, t(' — your name comes with it.')),
    );
    this.docketVeil = el('div', { class: 'docket-veil is-hidden' }, this.docket);
    // Clicking the desk around the slip puts it away — but only the desk, not the slip.
    this.docketVeil.addEventListener('click', (event) => {
      if (event.target === this.docketVeil) this.closeDocket();
    });

    // --- the question -----------------------------------------------------
    // Two cards of equal weight, because neither answer is the "real" one: half the people
    // who open this page were sent a code by somebody else.
    const createCard = el(
      'button',
      { class: 'choice-card choice-create', type: 'button', id: 'choose-create' },
      el('span', { class: 'choice-kicker' }, icon('flag', 'choice-icon'), t('Host')),
      el('span', { class: 'choice-title' }, t('Create a lobby')),
      el(
        'span',
        { class: 'choice-body' },
        t('Pick a tour or a city to race. You get a four-letter code to send to everyone else.'),
      ),
      el('span', { class: 'choice-go' }, t('Choose what to race'), icon('arrow_forward')),
    );
    createCard.addEventListener('click', () => this.showStage('create'));

    this.chooser = el(
      'div',
      { class: 'front-choice', id: 'front-choice' },
      createCard,
      el(
        'div',
        { class: 'choice-card choice-join' },
        el('span', { class: 'choice-kicker' }, icon('group', 'choice-icon'), t('Guest')),
        el('label', { class: 'choice-title', htmlFor: 'lobby-code' }, t('Join a lobby')),
        el('span', { class: 'choice-body' }, t('Somebody sent you a code? Type it here.')),
        el('div', { class: 'join-slip' }, this.codeInput, joinButton),
      ),
    );

    const back = el(
      'button',
      { class: 'icon-button front-back', type: 'button', id: 'front-back', title: t('Back') },
      icon('arrow_back'),
    );
    back.addEventListener('click', () => this.showStage('choose'));
    this.createView = el(
      'div',
      { class: 'front-create is-hidden', id: 'front-create' },
      el(
        'div',
        { class: 'front-create-head' },
        back,
        el('h2', { class: 'front-create-title' }, t('Create a lobby — what are we racing?')),
      ),
      this.tabStrip,
      this.shelf,
    );

    this.root = el(
      'div',
      { class: 'screen screen-front' },
      el(
        'div',
        { class: 'sheet' },
        el(
          'header',
          { class: 'masthead' },
          el('h1', { class: 'wordmark' }, 'OSM ', el('span', { class: 'wordmark-accent' }, 'Racer')),
          el('div', { class: 'masthead-tools' }, languageSwitch(), this.badgeSlot),
        ),
        this.errorNode,
        this.chooser,
        this.popular,
        this.createView,
      ),
      this.docketVeil,
    );

    this.select('tours');
  }

  /**
   * Opens on whichever shelf has something on it.
   *
   * A fresh server has no tours at all, and landing on an empty shelf that says "no tours
   * yet" is the worst possible first frame for a game whose whole pitch is the places you
   * can drive. Only ever moves off the default before anybody has touched a tab.
   */
  private chooseOpeningTab(): void {
    if (this.touched || this.tours.length || !this.fixtures.length) return;
    this.select('quick');
  }

  // -------------------------------------------------------------------------
  // data in
  // -------------------------------------------------------------------------

  setFixtures(fixtures: FixtureInfo[]): void {
    this.fixtures = fixtures;
    this.paintTabs();
    this.chooseOpeningTab();
    if (this.tab === 'quick') this.renderShelf();
  }

  setTours(tours: TourSummary[]): void {
    this.tours = tours;
    this.paintTabs();
    this.chooseOpeningTab();
    this.renderShelf();
    this.renderPopular();
  }

  setSignedIn(signedIn: boolean): void {
    if (this.signedIn === signedIn) return;
    this.signedIn = signedIn;
    if (this.tab === 'yours') this.renderShelf();
  }

  /**
   * A deep link's code. It lands in the join card on the opening question rather than
   * skipping it: the link already answered "join", but the name is still asked, and a page
   * that opens on a filled-in box with the button lit says exactly what will happen next.
   */
  setCode(code: string): void {
    this.codeInput.value = code;
    if (code) this.showStage('choose');
  }

  get currentStage(): Stage {
    return this.stage;
  }

  private showStage(stage: Stage): void {
    this.stage = stage;
    this.chooser.classList.toggle('is-hidden', stage !== 'choose');
    this.popular.classList.toggle('is-hidden', stage !== 'choose' || !this.tours.length);
    this.createView.classList.toggle('is-hidden', stage !== 'create');
    this.showError('');
    // The shelf's sketches are only fetched once somebody is looking at it.
    if (stage === 'create' && this.tab === 'quick') void this.drawFixtures();
  }

  showError(message: string): void {
    this.errorNode.textContent = message;
  }

  /** Escape closes the docket before it reaches the pause menu. */
  get isDocketOpen(): boolean {
    return !this.docketVeil.classList.contains('is-hidden');
  }

  closeDocket(): void {
    this.docketVeil.classList.add('is-hidden');
    this.pending = null;
  }

  // -------------------------------------------------------------------------
  // shelves
  // -------------------------------------------------------------------------

  private select(tab: Tab): void {
    this.tab = tab;
    for (const node of this.tabStrip.querySelectorAll('.sheet-tab')) {
      node.classList.toggle('is-active', (node as HTMLElement).dataset.tab === tab);
    }
    this.renderShelf();
    if (tab === 'quick' && this.stage === 'create') void this.drawFixtures();
  }

  private paintTabs(): void {
    const counts: Record<Tab, number> = {
      tours: this.tours.length,
      quick: this.fixtures.length,
      yours: this.tours.filter((tour) => tour.mine).length,
    };
    for (const node of this.tabStrip.querySelectorAll('.sheet-tab')) {
      const key = (node as HTMLElement).dataset.tab as Tab;
      const count = node.querySelector('.sheet-tab-count');
      if (count) count.textContent = counts[key] ? String(counts[key]) : '';
    }
  }

  private renderShelf(): void {
    clear(this.shelf);
    this.artNodes.clear();
    if (this.tab === 'tours') this.renderTours();
    else if (this.tab === 'quick') this.renderQuick();
    else this.renderYours();
  }

  /**
   * The most popular tours, under the two opening questions.
   *
   * The server sends the list already ranked (see `popularity` in `tourStore.ts`), so this is
   * just its head. On a fresh server nobody has voted and the head is the curated cups, which
   * is the right thing for a first visit to land on: places to race, one click away, rather
   * than a question about lobbies with nothing behind it.
   */
  private renderPopular(): void {
    clear(this.popular);
    const top = this.tours.slice(0, POPULAR_COUNT);
    this.popular.classList.toggle('is-hidden', this.stage !== 'choose' || !top.length);
    if (!top.length) return;
    const all = el('button', { class: 'button button-ghost', type: 'button', id: 'popular-all' }, t('All tours'), icon('arrow_forward'));
    all.addEventListener('click', () => {
      this.touched = true;
      this.showStage('create');
      this.select('tours');
    });
    const grid = el('div', { class: 'shelf-grid' });
    for (const tour of top) grid.append(this.tourCard(tour));
    this.popular.append(
      el('div', { class: 'front-popular-head' }, el('h2', { class: 'front-popular-title' }, t('Most popular tours')), all),
      grid,
    );
  }

  private renderTours(): void {
    this.shelf.append(el('div', { class: 'shelf-bar' }, this.searchInput));

    const visible = this.tours.filter((tour) => this.matches(tour));
    const grid = el('div', { class: 'shelf-grid' });
    for (const tour of visible) grid.append(this.tourCard(tour));
    this.shelf.append(grid);

    if (!visible.length) {
      this.shelf.append(
        el(
          'p',
          { class: 'shelf-empty' },
          this.tours.length
            ? t('Nothing matches that.')
            : t('No tours yet. Build one and it is on this shelf for everybody.'),
        ),
      );
    }
  }

  private renderQuick(): void {
    const grid = el('div', { class: 'shelf-grid' });
    for (const fixture of this.fixtures) grid.append(this.fixtureCard(fixture));
    grid.append(
      this.actionCard(t('Draw your own'), t('Any streets on Earth, step by step'), 'draw', () =>
        this.options.onDraw(),
      ),
    );
    this.shelf.append(grid);
  }

  private renderYours(): void {
    if (!this.signedIn) {
      const button = el('button', { class: 'button button-primary', type: 'button' }, t('Sign in'));
      button.addEventListener('click', () => this.options.onSignIn());
      this.shelf.append(
        el(
          'div',
          { class: 'shelf-signin' },
          el('p', { class: 'shelf-intro' }, t('Sign in to build tours of your own.')),
          button,
        ),
      );
      return;
    }

    const mine = this.tours.filter((tour) => tour.mine);
    const grid = el('div', { class: 'shelf-grid' });
    for (const tour of mine) grid.append(this.tourCard(tour));
    grid.append(
      this.actionCard(t('Build a tour'), t('Pick the cities, order the races'), 'library_add', () =>
        this.options.onNewTour(),
      ),
    );
    this.shelf.append(grid);
  }

  // -------------------------------------------------------------------------
  // cards
  // -------------------------------------------------------------------------

  private tourCard(tour: TourSummary): HTMLElement {
    const card = el(
      'button',
      { class: 'entry-card', type: 'button', dataset: { tour: tour.id } },
      el(
        'span',
        { class: 'entry-art' },
        previewSvg(tour.preview),
        // A cup saved through the builder always has a sketch; one posted to the API, or
        // saved before sketches existed, has none. Ruled paper with nothing on it reads as
        // a picture that failed to load, so it says which it is.
        !tour.preview && el('span', { class: 'entry-art-none' }, t('not surveyed')),
      ),
      el(
        'span',
        { class: 'entry-body' },
        el('span', { class: 'entry-name' }, tour.name),
        el(
          'span',
          { class: 'entry-meta' },
          plural(tour.stopCount, '{n} race', '{n} races'),
          el('span', { class: 'entry-meta-dot' }, '·'),
          tour.owner ?? tour.author ?? t('unknown'),
        ),
        el('span', { class: 'entry-stops' }, tour.stopTitles.join(' → ')),
        tour.frozen &&
          el(
            'span',
            { class: 'entry-badges' },
            el('span', { class: 'stamp' }, tour.curated ? t('curated') : t('archived')),
          ),
      ),
    );
    card.addEventListener('click', () =>
      this.choose({ kind: 'tour', id: tour.id, title: tour.name }),
    );

    // A button inside a button is not legal HTML, so the thumbs and the pencil are siblings
    // laid over the card. The pencil is only offered on your own, because it is the only
    // case the server will accept.
    const wrap = el('div', { class: 'entry-wrap' }, card, this.voteBar(tour));
    if (!tour.mine) return wrap;
    const edit = el(
      'button',
      { class: 'entry-edit', type: 'button', title: t('Edit {name}', { name: tour.name }), dataset: { tourEdit: tour.id } },
      icon('edit'),
    );
    edit.setAttribute('aria-label', t('Edit {name}', { name: tour.name }));
    edit.addEventListener('click', (event) => {
      event.stopPropagation();
      this.options.onEditTour(tour.id);
    });
    wrap.append(edit);
    return wrap;
  }

  /**
   * Thumbs up and down, with the counts beside them.
   *
   * Counts are shown to everybody; voting needs an account, and pressing one signed out
   * opens the sign-in rather than doing nothing. Pressing the one you already chose takes it
   * back. On your own tour they are shown and disabled — the server refuses those anyway.
   */
  private voteBar(tour: TourSummary): HTMLElement {
    const bar = el('div', { class: 'entry-votes', dataset: { tourVotes: tour.id } });
    for (const value of [1, -1] as const) {
      const chosen = tour.myVote === value;
      const count = value === 1 ? tour.up : tour.down;
      const name = value === 1 ? (chosen ? 'thumb_up_fill' : 'thumb_up') : chosen ? 'thumb_down_fill' : 'thumb_down';
      const label =
        value === 1 ? t('Thumbs up ({n})', { n: num(count) }) : t('Thumbs down ({n})', { n: num(count) });
      const button = el(
        'button',
        {
          class: `vote-button${chosen ? ' is-chosen' : ''}`,
          type: 'button',
          title: tour.mine ? t('You cannot vote on your own tour.') : label,
          dataset: { vote: String(value) },
        },
        icon(name),
        el('span', { class: 'vote-count' }, num(count)),
      );
      button.setAttribute('aria-label', label);
      button.setAttribute('aria-pressed', String(chosen));
      button.disabled = tour.mine;
      button.addEventListener('click', (event) => {
        event.stopPropagation();
        void this.vote(tour, chosen ? 0 : value);
      });
      bar.append(button);
    }
    return bar;
  }

  private async vote(tour: TourSummary, vote: -1 | 0 | 1): Promise<void> {
    if (!this.signedIn) {
      this.options.onSignIn();
      return;
    }
    const result = await this.options.onVote(tour.id, vote);
    if ('error' in result) {
      this.showError(result.error);
      return;
    }
    this.showError('');
    // The card keeps its place: re-ranking under the pointer would move the thing just
    // clicked. The order catches up the next time the list is loaded.
    for (const row of this.tours) if (row.id === tour.id) Object.assign(row, result);
    this.renderShelf();
    this.renderPopular();
  }

  private fixtureCard(fixture: FixtureInfo): HTMLElement {
    const art = el('span', { class: 'entry-art' }, previewSvg(this.sketches.get(fixture.id)));
    this.artNodes.set(fixture.id, art);
    const card = el(
      'button',
      { class: 'entry-card', type: 'button', dataset: { fixtureCard: fixture.id } },
      art,
      el(
        'span',
        { class: 'entry-body' },
        el('span', { class: 'entry-name' }, serverText(fixture.title)),
        el('span', { class: 'entry-meta' }, `${num(fixture.areaKm2)} km²`),
        el('span', { class: 'entry-stops' }, serverText(fixture.blurb)),
      ),
    );
    card.addEventListener('click', () =>
      this.choose({
        kind: 'region',
        title: fixture.title,
        region: { fixture: fixture.id, bbox: fixture.bbox, seed: 1 },
      }),
    );
    return card;
  }

  private actionCard(title: string, sub: string, mark: IconName, onClick: () => void): HTMLElement {
    const card = el(
      'button',
      { class: 'entry-card is-action', type: 'button' },
      el('span', { class: 'entry-action-mark' }, icon(mark)),
      el('span', { class: 'entry-name' }, title),
      el('span', { class: 'entry-meta' }, sub),
    );
    card.addEventListener('click', onClick);
    return card;
  }

  /**
   * Fills in the built-in regions' sketches, one at a time.
   *
   * Sequentially rather than all at once: on a server that has never generated these, each
   * one is real work, and four concurrent world builds on a one-vCPU box is how you turn a
   * decorative thumbnail into a stall. Nothing waits on this — the cards are already on
   * screen and clickable, and the picture drops into whichever one is still there.
   */
  private async drawFixtures(): Promise<void> {
    if (this.sketching) return;
    this.sketching = true;
    try {
      for (const fixture of this.fixtures) {
        if (this.sketches.has(fixture.id)) continue;
        try {
          const result = await this.options.onPreview({
            fixture: fixture.id,
            bbox: fixture.bbox,
            seed: 1,
          });
          if (!result.ok) continue;
          const sketch = buildStopPreview(result.preview ?? [], result.route ?? []);
          if (!sketch) continue;
          this.sketches.set(fixture.id, sketch);
          const art = this.artNodes.get(fixture.id);
          if (art) {
            clear(art);
            art.append(previewSvg(sketch));
          }
        } catch {
          // A card with no picture on it, which is what it looked like a moment ago anyway.
        }
      }
    } finally {
      this.sketching = false;
    }
  }

  private matches(tour: TourSummary): boolean {
    if (!this.query) return true;
    const haystack = fold(
      [tour.name, tour.owner ?? tour.author, ...tour.stopTitles].filter(Boolean).join(' '),
    );
    return this.query.split(/\s+/).every((word) => haystack.includes(word));
  }

  // -------------------------------------------------------------------------
  // the docket
  // -------------------------------------------------------------------------

  /**
   * Picking something off the shelf.
   *
   * The name and colour are asked for exactly once. Somebody who has already answered goes
   * straight from the card to the grid, which is the whole point of putting the question
   * after the choice rather than in front of it — and the chip in the masthead is there for
   * the day they want a different colour.
   */
  /** What the race wizard drew, entered the same way as a card off the shelf. */
  chooseRegion(region: RegionRequest, title: string): void {
    this.choose({ kind: 'region', region, title });
  }

  private choose(pick: Pick): void {
    if (this.name.trim()) {
      this.options.onStart(pick, this.name.trim(), this.color);
      return;
    }
    this.openDocket({ mode: 'start', pick });
  }

  private join(): void {
    const code = this.codeInput.value.trim().toUpperCase();
    if (code.length !== 4) {
      this.showError(t('A lobby code is four characters.'));
      return;
    }
    this.showError('');
    if (this.name.trim()) {
      this.options.onJoin(code, this.name.trim(), this.color);
      return;
    }
    this.openDocket({ mode: 'join', code });
  }

  private openDocket(pending: NonNullable<FrontDoor['pending']>): void {
    this.pending = pending;
    this.docketError.textContent = '';
    this.nameInput.value = this.name;
    this.renderSwatches();

    if (pending.mode === 'start') {
      this.docketHead.textContent = t('Race entry');
      clear(this.docketFor);
      this.docketFor.append(
        t('Entering '),
        el('b', {}, pending.pick.title),
      );
      this.docketGo.textContent = t('Start a lobby');
    } else if (pending.mode === 'join') {
      this.docketHead.textContent = t('Joining a race');
      clear(this.docketFor);
      this.docketFor.append(t('Lobby '), el('b', {}, pending.code));
      this.docketGo.textContent = t('Join the lobby');
    }

    this.docketVeil.classList.remove('is-hidden');
    this.nameInput.focus();
    this.nameInput.select();
  }

  private confirm(): void {
    const name = this.nameInput.value.trim().slice(0, 16);
    if (!name) {
      this.docketError.textContent = t('A driver needs a name.');
      this.nameInput.focus();
      return;
    }
    this.name = name;
    const pending = this.pending;
    this.closeDocket();
    this.options.onDriverChange(name, this.color);

    if (!pending) return;
    if (pending.mode === 'start') this.options.onStart(pending.pick, name, this.color);
    else this.options.onJoin(pending.code, name, this.color);
  }

  private renderSwatches(): void {
    clear(this.swatches);
    for (const color of CAR_COLORS) {
      const button = el('button', {
        class: `swatch-button${color === this.color ? ' is-active' : ''}`,
        type: 'button',
        style: { background: hex(color) },
        title: t('Car colour {hex}', { hex: hex(color) }),
        dataset: { color: String(color) },
      });
      button.addEventListener('click', () => {
        this.color = color;
        for (const other of this.swatches.querySelectorAll('.swatch-button')) {
          other.classList.toggle('is-active', other === button);
        }
      });
      this.swatches.append(button);
    }
  }

  /** The driver as the account panel last left it, so the docket opens on the same answer. */
  setDriver(name: string, color: number): void {
    this.name = name;
    this.color = color;
    // Signing in from the docket's own link fills the name in underneath it.
    if (this.isDocketOpen) {
      this.nameInput.value = name;
      this.renderSwatches();
    }
  }
}

/**
 * Case- and accent-insensitive, so searching "krakow" finds "Kraków".
 *
 * NFD splits a letter from its diacritic and the range below removes the diacritic, which
 * is the whole trick — and the reason it is done here rather than with `localeCompare`,
 * which answers a different question.
 */
function fold(text: string): string {
  return text
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
}
